import { faPause } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Button,
  FlexBox,
  JoonUIColor,
  SPACING,
  TextButton,
  Typography,
  Loader,
  Modal,
  ModalHeader,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useState } from "react"
import toast from "react-hot-toast"
import { Trans, useTranslation } from "react-i18next"

import CancelModal, { useCancelModalStore } from "./cancelModal/CancelModal"
import useSettingsSubscriptionModalStore from "./hooks/useSettingsSubscriptionModalStore"
import useChurnkeyStyles from "./useChurnkeyStyles"
import useIsCoachingAvailable from "../../hooks/useIsCoachingAvailable"
import { useGetCoachSubscriptionQuery } from "../../networking/coaching/queries"
import { getCancelFlow } from "../../networking/families/api"
import {
  useFamilyQuery,
  useSubscriberInfo,
  useUserQuery,
} from "../../networking/queries"
import { getCoachingStripeBillingUrl } from "../../networking/subscriptions"
import { SubscriptionStatus } from "../../types"
import { CancelFlowModalType } from "../../types/cancelFlow"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { useRouter } from "../../util/router"
import { getCurrentSubscription, getSubscriptionType } from "../../util/util"
import useUnpauseConfirmationModalStore from "../coach/overlays/useUnpauseConfirmationModalStore"
import usePlanUpdateModalStore from "../coach/planUpdate/usePlanUpdateModalStore"
import { useCheckoutRedirect } from "../paywall/useCheckoutRedirect"

dayjs.extend(utc)

const SettingsSubscriptionModal = () => {
  const { user } = useUserQuery()
  const { isOpen, onClose } = useSettingsSubscriptionModalStore()
  const { t } = useTranslation()

  useChurnkeyStyles()

  if (!user) return <></>

  return (
    <Modal isOpen={isOpen} onClose={onClose} animate mobileFull>
      <ModalHeader title={t("SUB_TITLE")} onClose={onClose} showBorderBottom />
      <div className="flex flex-col gap-6 p-6 pt-4 overflow-y-auto">
        <FamilySubscriptionSection />
        <CoachSubscriptionSection />
      </div>
      <CancelModal />
    </Modal>
  )
}

export default SettingsSubscriptionModal

const FamilySubscriptionSection = () => {
  const [isLoadingCancelJoonityFlow, setIsLoadingCancelJoonityFlow] =
    useState(false)
  const {
    isLoadingCheckoutFn,
    redirectToFamilyBilling,
    redirectToFamilyCheckout,
  } = useCheckoutRedirect()
  const { onOpen: onOpenCancelModal } = useCancelModalStore()
  const { data: subscriberData, isLoading } = useSubscriberInfo()
  const { data: family } = useFamilyQuery()
  const { t } = useTranslation()

  const currentSubscription = getCurrentSubscription(subscriberData)
  const hasUnsubscribed = currentSubscription?.unsubscribe_detected_at !== null
  const expiresDate =
    subscriberData?.subscriber?.entitlements?.premium?.expires_date
  const hasExpiredPlan = dayjs(expiresDate).unix() < dayjs().unix()
  const planType = getSubscriptionType(currentSubscription)
  const renewal = currentSubscription?.expires_date

  const handleCancelSubscription = async () => {
    if (!family) {
      toast.error(t("SUB_TOAST_CANCEL_ERROR"))
      return
    }
    setIsLoadingCancelJoonityFlow(true)
    const steps = await getCancelFlow(CancelFlowModalType.JOONITY, family.id)
    setIsLoadingCancelJoonityFlow(false)
    if (steps.length === 0) {
      toast.error(t("SUB_TOAST_CANCEL_ERROR"))
      return
    }
    onOpenCancelModal({ steps, subscriptionType: CancelFlowModalType.JOONITY })
  }

  return (
    <div
      style={{
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
        background: JoonUIColor.background.primaryNeutral,
      }}
    >
      <div className="flex justify-between items-center bg-bgXLightGray px-6 py-3 border-b border-borderPrimary rounded-t-md">
        <Typography variant="h3">{t("SUB_JOON_APP")}</Typography>
      </div>
      {isLoading ? (
        <FlexBox
          direction="column"
          align="center"
          style={{ padding: SPACING.space4 }}
        >
          <Loader size={20} thickness={3} color={JoonUIColor.icon.accent} />
        </FlexBox>
      ) : !subscriberData?.active_plan?.is_active ? (
        <FlexBox
          direction="column"
          gap={SPACING.space4}
          align="center"
          style={{ padding: `${SPACING.space3} ${SPACING.space6}` }}
        >
          <Typography variant="bodyBold" textAlign="center">
            {t("SUB_SUBSCRIBE")}
          </Typography>
          <Typography
            variant="bodySmall"
            textAlign="center"
            style={{ maxWidth: "440px" }}
          >
            {t("SUB_SUBSCRIBE_DESC")}
          </Typography>
          <img src="/images/previews/child_app_preview.svg" alt="" />
          <Button
            text={t("SUB_PURCHASE")}
            isLoading={isLoadingCheckoutFn}
            onClick={() => redirectToFamilyCheckout(undefined, false)}
            fullWidth
          />
        </FlexBox>
      ) : (
        <>
          <FlexBox
            direction="row"
            justify="space-between"
            wrap={false}
            style={{
              padding: `${SPACING.space3} ${SPACING.space6}`,
              background: JoonUIColor.background.primaryNeutral,
              borderBottom: `1px solid ${JoonUIColor.border.default}`,
            }}
          >
            <FlexBox direction="column">
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.secondary}
              >
                {t("SUB_TYPE")}
              </Typography>
              <Typography variant="bodySmallBold">
                {subscriberData?.active_plan?.is_active
                  ? planType[0]?.toUpperCase() + planType.slice(1)
                  : t("SUB_NONE")}
              </Typography>
            </FlexBox>
            <FlexBox direction="column">
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.secondary}
              >
                {hasExpiredPlan
                  ? t("SUB_EXPIRED_ON")
                  : hasUnsubscribed
                    ? t("SUB_EXPIRING_ON")
                    : t("SUB_RENEWING_ON")}
              </Typography>
              <Typography variant="bodySmallBold">
                {renewal ? dayjs.utc(renewal)?.format("MMMM D, YYYY") : "-"}
              </Typography>
            </FlexBox>
          </FlexBox>
          <FlexBox
            direction="row"
            justify="space-around"
            style={{ padding: `${SPACING.space3} ${SPACING.space6}` }}
          >
            {currentSubscription?.store === "stripe" ? (
              <FlexBox
                direction="row"
                gap={SPACING.space2}
                justify="space-around"
              >
                {isLoadingCheckoutFn || isLoadingCancelJoonityFlow ? (
                  <Loader
                    size={20}
                    thickness={2}
                    color={JoonUIColor.icon.accent}
                  />
                ) : (
                  <>
                    {!hasUnsubscribed && (
                      <TextButton onClick={() => handleCancelSubscription()}>
                        <Typography
                          variant="bodySmallBold"
                          color={JoonUIColor.text.primaryAccent}
                        >
                          {t("QB_CANCEL")}
                        </Typography>
                      </TextButton>
                    )}
                    <TextButton onClick={() => redirectToFamilyBilling()}>
                      <Typography
                        variant="bodySmallBold"
                        color={JoonUIColor.text.primaryAccent}
                      >
                        {t("SUB_MANAGE")}
                      </Typography>
                    </TextButton>
                  </>
                )}
              </FlexBox>
            ) : (
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.secondary}
              >
                {t("SUB_OPEN_IN_APP")}
              </Typography>
            )}
          </FlexBox>
        </>
      )}
    </div>
  )
}

const CoachSubscriptionSection = () => {
  const [isLoadingCancelCoachFlow, setIsLoadingCancelCoachFlow] =
    useState(false)
  const { onOpen: onOpenUnpauseConfirmationModal } =
    useUnpauseConfirmationModalStore()
  const { onOpen: onOpenCancelModal } = useCancelModalStore()
  const { data: coachSubscriptionData, isLoading } =
    useGetCoachSubscriptionQuery()
  const { onClose } = useSettingsSubscriptionModalStore()
  const hasUnsubscribed = !!coachSubscriptionData?.set_to_cancel
  const renewal = coachSubscriptionData?.active_period_end_date
  const hasActivePlan = [
    coachSubscriptionData?.status === SubscriptionStatus.PAYING,
    coachSubscriptionData?.status === SubscriptionStatus.TRIAL,
  ].includes(true)
  const isPaused = !!coachSubscriptionData?.pause_collection_end_datetime
  const { onOpen: openUpgradePlanModal } = usePlanUpdateModalStore()
  const { t } = useTranslation()
  const isCoachingAvailable = useIsCoachingAvailable()

  const router = useRouter()

  const handleCancelSubscription = async () => {
    setIsLoadingCancelCoachFlow(true)
    const steps = await getCancelFlow(CancelFlowModalType.COACHING)
    setIsLoadingCancelCoachFlow(false)
    if (steps.length === 0) {
      toast.error(t("SUB_TOAST_CANCEL_ERROR"))
      return
    }
    onOpenCancelModal({
      steps,
      subscriptionType: CancelFlowModalType.COACHING,
    })
  }

  const handleGoToBilling = async () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_BILLING)
    const billingUrl = await getCoachingStripeBillingUrl()
    window.open(billingUrl, "_self")
  }

  const handleChangePlan = () => {
    onClose()
    openUpgradePlanModal()
  }

  const handleUnpause = () => {
    onOpenUnpauseConfirmationModal()
  }

  // Without an active plan this section is nothing but the coaching paywall.
  // Existing subscribers keep their management controls in any language.
  if (!hasActivePlan && !isCoachingAvailable) return <></>

  return (
    <div
      style={{
        border: `1px solid ${JoonUIColor.border.default}`,
        borderRadius: SPACING.space2,
        background: JoonUIColor.background.primaryNeutral,
      }}
    >
      <div className="flex flex-col gap-1 items-center bg-bgXLightGray px-6 py-3 border-b border-borderPrimary rounded-t-md w-full">
        <div className="flex justify-between items-center w-full">
          <Typography variant="h3">
            Joon{" "}
            <Typography
              variant="h3"
              color={JoonUIColor.text.primaryAccent}
              style={{ fontFamily: "Caveat", fontSize: "24px" }}
            >
              Plus{" "}
            </Typography>
            Coach
          </Typography>

          {hasActivePlan && coachSubscriptionData?.price_per_seat && (
            <Typography variant="bodySmallBold">
              {t("SUB_MONTHLY", {
                price: `$${(coachSubscriptionData.price_per_seat / 100).toFixed(2)}`,
              })}
            </Typography>
          )}
        </div>
        {isPaused && (
          <div className="flex flex-row gap-1 bg-red-400 items-center px-2 py-1 rounded-md w-fit">
            <FontAwesomeIcon
              icon={faPause}
              color={JoonUIColor.icon.inverted}
              fontSize={12}
            />
            <Typography
              variant="bodySmall"
              textAlign="center"
              color={JoonUIColor.text.inverted}
              className="w-fit"
            >
              {t("SUB_PAUSED_UNTIL", {
                date: dayjs(
                  coachSubscriptionData?.pause_collection_end_datetime
                ).format("MMM D"),
              })}
            </Typography>
          </div>
        )}
      </div>
      {isLoading ? (
        <FlexBox
          direction="column"
          align="center"
          style={{ padding: SPACING.space4 }}
        >
          <Loader size={20} thickness={3} color={JoonUIColor.icon.accent} />
        </FlexBox>
      ) : hasActivePlan ? (
        <>
          <FlexBox
            direction="row"
            wrap={false}
            style={{
              padding: `${SPACING.space3} ${SPACING.space6}`,
              background: JoonUIColor.background.primaryNeutral,
              borderBottom: `1px solid ${JoonUIColor.border.default}`,
            }}
          >
            <FlexBox direction="column">
              <Typography
                variant="bodySmallBold"
                color={JoonUIColor.text.secondary}
              >
                {hasUnsubscribed ? t("SUB_EXPIRING_ON") : t("SUB_RENEWING_ON")}
              </Typography>
              <Typography variant="bodySmallBold">
                {renewal ? dayjs.utc(renewal)?.format("MMMM D, YYYY") : "-"}
              </Typography>
            </FlexBox>
          </FlexBox>
          <FlexBox
            direction="row"
            justify="space-around"
            wrap={false}
            style={{ padding: `${SPACING.space3} ${SPACING.space6}` }}
            fullWidth
          >
            {isLoadingCancelCoachFlow ? (
              <Loader size={20} thickness={2} color={JoonUIColor.icon.accent} />
            ) : (
              <>
                {!hasUnsubscribed && (
                  <TextButton onClick={handleCancelSubscription}>
                    <Typography
                      variant="bodySmallBold"
                      color={JoonUIColor.text.primaryAccent}
                    >
                      {t("QB_CANCEL")}
                    </Typography>
                  </TextButton>
                )}
                {isPaused ? (
                  <TextButton onClick={handleUnpause}>
                    <Typography
                      variant="bodySmallBold"
                      color={JoonUIColor.text.primaryAccent}
                    >
                      {t("SUB_UNPAUSE")}
                    </Typography>
                  </TextButton>
                ) : (
                  <>
                    <TextButton onClick={handleGoToBilling}>
                      <Typography
                        variant="bodySmallBold"
                        color={JoonUIColor.text.primaryAccent}
                      >
                        {t("SUB_MANAGE")}
                      </Typography>
                    </TextButton>
                    <TextButton onClick={handleChangePlan}>
                      <Typography
                        variant="bodySmallBold"
                        color={JoonUIColor.text.primaryAccent}
                      >
                        {t("SUB_CHANGE_PLAN")}
                      </Typography>
                    </TextButton>
                  </>
                )}
              </>
            )}
          </FlexBox>
        </>
      ) : (
        <>
          <FlexBox
            direction="column"
            align="center"
            gap={SPACING.space4}
            style={{ padding: `${SPACING.space3} ${SPACING.space6}` }}
          >
            <Typography variant="bodyBold" textAlign="center">
              {t("COACH_PAYWALL_HEADLINE")}
            </Typography>
            <Typography variant="bodySmall" textAlign="center">
              <Trans
                i18nKey="SUB_COACH_SUBTEXT"
                components={{
                  b: <Typography variant="bodySmallBold">{""}</Typography>,
                }}
              />
            </Typography>
            <img
              src="/images/guidePhotos/coach_main_headline.svg"
              style={{ width: "70%" }}
              alt="~~~"
            />
            <Button
              text={t("SUB_LEARN_MORE")}
              fullWidth
              onClick={() => {
                onClose()
                router.push("/coach")
              }}
            />
          </FlexBox>
        </>
      )}
    </div>
  )
}
